import { CONFIG } from "./config";
import {
  closeDebugPanel,
  debugToolLabel,
  getArmedDebugTool,
  initDebugPanel,
  disarmDebugTool,
  refreshDebugPanel,
  toggleDebugPanel,
} from "./debug";
import { type Axial, type Point, hexKey, parseHexKey } from "./hexgrid";
import { closeInspector, type InspectorCallbacks, renderInspector } from "./inspector";
import { type Camera, type SafeArea, type Selection, hexToScreen, makeCamera, render, screenToHex } from "./render";
import { hasSave, loadGame, loadGameFromSlot, saveGame } from "./save";
import { closeSavesPanel, initSavesPanel, toggleSavesPanel } from "./saves";
import {
  advanceTurn,
  buildBattery,
  buildControlNode,
  buildLine,
  createInitialGameState,
  debugPlaceNode,
  debugRemoveNode,
  debugSetBatteryBudget,
  debugSetControlNodeBudget,
  debugSetLineBudgetKm,
  debugSetNodeCapacity,
  joinControlNodeLines,
  previewSolve,
  setProducerDial,
  setStorageMode,
  splitControlNodeLines,
  toggleBattery,
  toggleControlNode,
  toggleLine,
} from "./simulation";
import type { GameState } from "./state";
import { updateHud } from "./hud";

type BuildMode = "line" | "control" | "battery" | "inspect";

const canvas = document.getElementById("game-canvas") as HTMLCanvasElement;
const ctx = canvas.getContext("2d");
if (!ctx) throw new Error("2D context not available");

let camera: Camera = makeCamera(window.innerWidth, window.innerHeight);
let state: GameState = hasSave() ? (loadGame() ?? createInitialGameState(Date.now())) : createInitialGameState(Date.now());

const selection: Selection = { pendingHex: null, hoverHex: null, inspectedNodeId: null, inspectedLineId: null };
let buildMode: BuildMode = "line";

const hudEl = document.getElementById("hud") as HTMLDivElement;
const hudControlsEl = document.getElementById("hud-controls") as HTMLDivElement;
// Small cushion beyond the HUD's/controls' own measured footprint so the
// map doesn't render flush against them.
const SAFE_AREA_PADDING = 12;

function computeSafeArea(): SafeArea {
  const hudRect = hudEl.getBoundingClientRect();
  const controlsRect = hudControlsEl.getBoundingClientRect();
  return {
    top: hudRect.bottom + SAFE_AREA_PADDING,
    bottom: window.innerHeight - controlsRect.top + SAFE_AREA_PADDING,
    left: 0,
    right: 0,
  };
}

function resize(): void {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  camera = makeCamera(canvas.width, canvas.height, computeSafeArea());
}
window.addEventListener("resize", resize);
// Browser/page zoom shrinks the viewport but doesn't reliably fire
// "resize" in every browser, and the HUD's own footprint can change
// independently of the window (e.g. a warning banner appearing) — a
// ResizeObserver on the HUD elements themselves catches both cases,
// since it fires whenever their rendered box actually changes size.
const resizeObserver = new ResizeObserver(() => resize());
resizeObserver.observe(hudEl);
resizeObserver.observe(hudControlsEl);
resize();

// --- toast ---
const toastEl = document.getElementById("toast") as HTMLDivElement;
let toastTimer: number | undefined;
function showToast(message: string): void {
  toastEl.textContent = message;
  toastEl.classList.remove("hidden");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.add("hidden"), 2000);
}

// --- build mode ---
const modeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>(".mode-btn"));
const helpTextEl = document.getElementById("hud-help-text") as HTMLSpanElement;
const MODE_HELP: Record<BuildMode, string> = {
  line: "Click a hex, then click an adjacent hex to build a line.",
  control: "Click an empty hex to build a control node.",
  battery: "Click an empty hex to build a battery.",
  inspect: "Click a node or line to inspect it.",
};
function setBuildMode(mode: BuildMode): void {
  buildMode = mode;
  selection.pendingHex = null;
  modeButtons.forEach((btn) => btn.classList.toggle("selected", btn.dataset.mode === mode));
  helpTextEl.textContent = MODE_HELP[mode];
}
modeButtons.forEach((btn) => btn.addEventListener("click", () => setBuildMode((btn.dataset.mode as BuildMode) ?? "line")));
setBuildMode("line");

// --- input ---
function canvasPoint(evt: MouseEvent): Point {
  const rect = canvas.getBoundingClientRect();
  return { x: evt.clientX - rect.left, y: evt.clientY - rect.top };
}

function isWithinMap(a: Axial): boolean {
  return Math.max(Math.abs(a.q), Math.abs(a.r), Math.abs(-a.q - a.r)) <= CONFIG.mapRadius;
}

function findLineAt(p: Point): string | null {
  const threshold = 8;
  for (const line of Object.values(state.lines)) {
    const a = hexToScreen(camera, parseHexKey(line.a));
    const b = hexToScreen(camera, parseHexKey(line.b));
    if (pointToSegmentDistance(p, a, b) <= threshold) return line.id;
  }
  return null;
}

function pointToSegmentDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

canvas.addEventListener("mousemove", (evt) => {
  const p = canvasPoint(evt);
  const hex = screenToHex(camera, p.x, p.y);
  selection.hoverHex = isWithinMap(hex) ? hex : null;
});

canvas.addEventListener("click", (evt) => {
  if (state.gameOver) return;
  const p = canvasPoint(evt);
  const lineId = findLineAt(p);
  const hex = screenToHex(camera, p.x, p.y);
  const hexInBounds = isWithinMap(hex);
  const hexId = hexInBounds ? hexKey(hex) : null;
  const nodeAtHex = hexId ? state.nodes[hexId] : undefined;

  // A debug tool, once armed, takes priority over every normal build mode —
  // see debug.ts's module comment for why that has to be unambiguous.
  const armedDebugTool = getArmedDebugTool();
  if (armedDebugTool) {
    if (armedDebugTool.action === "remove") {
      if (nodeAtHex) debugRemoveNode(state, nodeAtHex.id);
      return;
    }
    if (!hexInBounds) return;
    const result = debugPlaceNode(state, hex, armedDebugTool.spec);
    if (result === "occupied") showToast("Hex already occupied");
    return;
  }

  if (buildMode === "inspect") {
    if (nodeAtHex) {
      selection.inspectedNodeId = selection.inspectedNodeId === nodeAtHex.id ? null : nodeAtHex.id;
      selection.inspectedLineId = null;
      // Debug panel deliberately stays open here (see #debug-panel's CSS
      // comment) — only the saves panel is exclusive with the inspector.
      if (selection.inspectedNodeId) closeSavesPanel();
    } else if (lineId) {
      // Info only — no longer toggles dismantle here, that's Line mode's
      // job. See doc/TODOS.md.
      selection.inspectedLineId = selection.inspectedLineId === lineId ? null : lineId;
      selection.inspectedNodeId = null;
      if (selection.inspectedLineId) closeSavesPanel();
    } else {
      selection.inspectedNodeId = null;
      selection.inspectedLineId = null;
    }
    return;
  }

  if (buildMode === "control") {
    if (nodeAtHex?.kind === "control") {
      selection.inspectedNodeId = selection.inspectedNodeId === nodeAtHex.id ? null : nodeAtHex.id;
      selection.inspectedLineId = null;
      if (selection.inspectedNodeId) closeSavesPanel();
      return;
    }
    // Lines aren't dismantlable from this mode — that's "line" mode's job —
    // and a line merely passing near the click point (findLineAt's hit
    // radius) must not block placing a node on the hex underneath it, so
    // `lineId` is deliberately ignored here rather than short-circuiting.
    if (!hexInBounds) return;
    const result = buildControlNode(state, hex);
    if (result === "insufficient-budget") showToast("No control nodes left");
    else if (result === "occupied") showToast("Hex already occupied");
    return;
  }

  if (buildMode === "battery") {
    if (nodeAtHex?.kind === "storage" && nodeAtHex.source === "battery") {
      selection.inspectedNodeId = selection.inspectedNodeId === nodeAtHex.id ? null : nodeAtHex.id;
      selection.inspectedLineId = null;
      if (selection.inspectedNodeId) closeSavesPanel();
      return;
    }
    // Same reasoning as "control" mode above: a nearby line must not block
    // placing a battery on the hex underneath it.
    if (!hexInBounds) return;
    const result = buildBattery(state, hex);
    if (result === "insufficient-budget") showToast("No batteries left");
    else if (result === "occupied") showToast("Hex already occupied");
    return;
  }

  // "line" mode (default)
  if (lineId) {
    toggleLine(state, lineId);
    return;
  }
  if (!hexInBounds) return;

  if (!selection.pendingHex) {
    selection.pendingHex = hex;
    return;
  }
  if (selection.pendingHex.q === hex.q && selection.pendingHex.r === hex.r) {
    selection.pendingHex = null;
    return;
  }

  const result = buildLine(state, selection.pendingHex, hex);
  if (result === "insufficient-budget") showToast("Not enough line budget (km)");
  else if (result === "not-adjacent") showToast("Lines can only connect adjacent hexes");
  else if (result === "already-exists") showToast("A line already connects these hexes");
  selection.pendingHex = null;
});

window.addEventListener("keydown", (evt) => {
  if (evt.key === "Escape") {
    selection.pendingHex = null;
    selection.inspectedNodeId = null;
    selection.inspectedLineId = null;
    disarmDebugTool();
  }
});

// --- inspector wiring ---
const inspectorCallbacks: InspectorCallbacks = {
  onDialChange: (nodeId, phase, value) => setProducerDial(state, nodeId, phase, value),
  onJoinSelected: (hexId, lineIds) => joinControlNodeLines(state, hexId, lineIds),
  onSplitSelected: (hexId, lineIds) => splitControlNodeLines(state, hexId, lineIds),
  onSetStorageMode: (nodeId, mode) => setStorageMode(state, nodeId, mode),
  onDebugSetCapacity: (nodeId, capacityMw) => debugSetNodeCapacity(state, nodeId, capacityMw),
  onToggleDismantle: (hexId) => {
    const node = state.nodes[hexId];
    if (!node) return;
    if (node.kind === "control") toggleControlNode(state, hexId);
    else if (node.kind === "storage" && node.source === "battery") toggleBattery(state, hexId);
  },
  onClose: () => {
    selection.inspectedNodeId = null;
    selection.inspectedLineId = null;
  },
};

// --- debug panel wiring ---
initDebugPanel({
  onSetLineBudget: (km) => debugSetLineBudgetKm(state, km),
  onSetControlNodeBudget: (n) => debugSetControlNodeBudget(state, n),
  onSetBatteryBudget: (n) => debugSetBatteryBudget(state, n),
});
document.getElementById("btn-debug-toggle")?.addEventListener("click", () => {
  toggleDebugPanel(() => {
    // Opening the debug panel deliberately leaves the inspector alone
    // (they're positioned to not overlap, and staying open together is
    // what lets debug-editing a selected node's capacity work) — only the
    // saves panel is exclusive with it.
    closeSavesPanel();
  });
});

// --- saves panel wiring ---
initSavesPanel({
  getState: () => state,
  onLoadSlot: (id) => {
    const loaded = loadGameFromSlot(id);
    if (loaded) {
      state = loaded;
      selection.pendingHex = null;
      selection.inspectedNodeId = null;
      selection.inspectedLineId = null;
      closeInspector();
      closeSavesPanel();
      showToast("Loaded");
    } else {
      showToast("Couldn't load that save");
    }
  },
});
document.getElementById("btn-saves-toggle")?.addEventListener("click", () => {
  toggleSavesPanel();
  selection.inspectedNodeId = null;
  selection.inspectedLineId = null;
  closeInspector();
  closeDebugPanel();
});

// --- controls ---
document.getElementById("btn-next-turn")?.addEventListener("click", () => {
  advanceTurn(state);
  saveGame(state);
});
document.getElementById("btn-save")?.addEventListener("click", () => {
  saveGame(state);
  showToast("Saved");
});
document.getElementById("btn-new-game")?.addEventListener("click", () => {
  state = createInitialGameState(Date.now());
  saveGame(state);
  selection.pendingHex = null;
  selection.inspectedNodeId = null;
  selection.inspectedLineId = null;
  closeInspector();
  showToast("New game started");
});
document.getElementById("btn-restart")?.addEventListener("click", () => {
  state = createInitialGameState(Date.now());
  saveGame(state);
  selection.pendingHex = null;
  selection.inspectedNodeId = null;
  selection.inspectedLineId = null;
  closeInspector();
});

// --- render loop ---
// A single bad frame (e.g. a stale/incompatible save slipping past
// loadGame's checks, or any other bug) must not silently freeze the whole
// screen with no feedback — that's exactly what happened once already, and
// it's very hard to diagnose from the outside. Log it and keep trying every
// frame instead of letting the loop die.
let lastLoggedError: unknown;
function loop(): void {
  try {
    const preview = previewSolve(state);
    render(ctx!, state, preview, camera, selection);
    updateHud(state, preview);
    renderInspector(state, selection.inspectedNodeId, selection.inspectedLineId, preview, inspectorCallbacks);
    refreshDebugPanel(state);
    const armedDebugTool = getArmedDebugTool();
    helpTextEl.textContent = armedDebugTool ? debugToolLabel(armedDebugTool) : MODE_HELP[buildMode];
  } catch (err) {
    if (err !== lastLoggedError) {
      lastLoggedError = err;
      console.error("Render loop error — game state may be invalid. Try New Game.", err);
    }
  }
  requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
